import {
    API_ENDPOINTS,
    BottomAnchoredControls,
    Button,
    ButtonTypes,
    Card,
    CardBody,
    FormInput,
    getThemeFromUrl,
    PageLayout,
    PhoneNumberField,
    PostcodeLookup,
    replaceKeyWithValue,
    Roles,
    route,
    SelectableCardInput,
    SelectOption,
    Spinner,
    StringHelpers,
    SubTitle,
    Themes,
    useDisabledContext,
    useForm,
    useLangContext,
    useQueries,
    useScrollToError,
    useStore,
    useUserContext,
    Variants,
    WidthConstrainedContainer,
    GasSafeLookup,
    useAuthorisationContext,
    Sizes,
    Icons,
    MultiFileInput,
    customToast,
    FileHelpers,
    UploadDataType,
    checkTheme,
} from 'carrier-fe';
import React, { useEffect, useLayoutEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyStorePayload } from '../../../types/companies';
import axios from 'axios';
import { CompanyTypes } from '../../../Constants';
import { defaultData, handleError, initialSelectsState, selectsReducer } from './company-helpers';
import ViessmannGas from 'carrier-fe/src/images/viessmann_gas.jpg';
import ViessmannHeatpumps from 'carrier-fe/src/images/viessmann_heatpumps.jpg';
import ViessmannElectric from 'carrier-fe/src/images/viessmann_electric.svg';
import ViessmannSolar from 'carrier-fe/src/images/viessmann_solar.jpg';
import { UserType } from '../../../types/user';

const ColumnLayout = ({children}: { children: React.ReactNode }) => {
    return (
        <div className="col-12 col-md-6">
            <div>{children}</div>
        </div>
    );
};

function CompanyForm() {
    // Hooks and Contexts
    const {companyId} = useParams();
    const {user} = useUserContext();
    const {crud, fields} = useLangContext();
    const {disabled, setDisabled} = useDisabledContext();
    const { hasRoleAccess } = useAuthorisationContext();
    const navigate = useNavigate();

    const isSuperAdminOrPlatformManager = 
        hasRoleAccess(Roles.SUPER_ADMIN) || 
        hasRoleAccess(Roles.PLATFORM_MANAGER); 

    // State Management
    const {store: loaders, setValues: setLoaders} = useStore({
        fetchingGasSafe: false,
        fetchingCompany: false,
    });
    const [selects, dispatchSelects] = useReducer(selectsReducer, initialSelectsState);
	const [gasSafeNameMismatch, setGasSafeNameMismatch] = useState('');

    const [primaryIndividual, setPrimaryIndividual] = useState<SelectOption & { userId: string}>({
        label: '',
        value: '',
        userId: '',
    });
    const [isManualEntry, setIsManualEntry] = useState<boolean>(false);

    // Queries
    const {data, isLoading} = useQueries([
        {
            key: 'distributor_claim_types',
            config: {
                method: 'GET',
                url: route(API_ENDPOINTS.GENERIC.SELECT.DISTRIBUTOR_CLAIM_TYPES),
            },
        },
        {
            key: 'roles',
            config: {
                method: 'GET',
                url: route(
                    API_ENDPOINTS.GENERIC.SELECT.COMPANY_TYPE_ROLES + `?type=${CompanyTypes.INSTALLER}` // By default fetch installer roles
                ),
            },
        },
        ...(companyId ? [{
            key: 'company_users',
            config: {
                method: 'GET',
                url: route(
                    API_ENDPOINTS.ADMIN.COMPANY.USERS,
                    { companyId: companyId }
                ),
            },
        }] : []),
        {
            key: 'company_document_types',
            config: {
                method: 'GET',
                url: route(
                    API_ENDPOINTS.GENERIC.SELECT.DOCUMENT_TYPES.COMPANY
                ),
            }
        }
    ]);

    const documentTypes: SelectOption[] = data?.company_document_types?.data;

    // filter company users that are not the primary individual, deleted, or company managers
    const companyUsers: SelectOption[] = data?.company_users?.data?.rows
        ?.filter((user: UserType) =>
            StringHelpers.lower(user?.id) !== StringHelpers.lower(primaryIndividual.userId)
            && user?.deleted_at === null
            && user?.role_name_display === 'Company Manager'
        ).map((user: UserType) => ({
            label: user?.name, 
            value: user?.id 
        })) || [];
 
    if (primaryIndividual.label && primaryIndividual.value) {
        const { label, value } = primaryIndividual;
        companyUsers.unshift({
            label: `${label} (Current)`,
            value,
        })
    }

    // Derived States
    const isEdit = Boolean(companyId);
    const componentLoading = useMemo(
        () => isLoading || loaders?.fetchingCompany,
        [isLoading, loaders?.fetchingCompany]
    );
    const booleanOptions = useMemo(
        () =>
            crud?.options?.boolean?.map((option: SelectOption) => ({
                ...option,
                value: Number(option.value) === 1,
            })),
        [crud?.options?.boolean]
    );
    const title = useMemo(
        () =>
            isEdit
                ? replaceKeyWithValue(
                    crud?.pages?.edit.title || 'Edit :model',
                    'model',
                    crud?.models?.company || 'Company'
                )
                : replaceKeyWithValue(
                    crud?.pages?.create.title || 'Create :model',
                    'model',
                    crud?.models?.company || 'Company'
                ),
        [crud, isEdit]
    );
    const themes = useMemo(() => {
        return {
            isToshiba: getThemeFromUrl() === Themes.Toshiba,
            isViessmann: getThemeFromUrl() === Themes.Viessmann,
        };
    }, []);

    // Handlers
    const handleSubmit = async (data: Partial<CompanyStorePayload>): Promise<any> => {
        // remove dynamic fields
        if(!data?.gas_boiler_systems_enabled){
            delete data?.['gas_safe_licence_card_number']
            delete data?.['gas_safe_confirmed']
            delete data?.['gas_safe_number']
            delete data?.['user_gas_safe_confirmed']
        }
        if(!data?.solar_systems_enabled && !data?.heat_pump_systems_enabled){
            delete data?.['mcs_number']
        }
        if(data?.sap_number) {
            data['heat_pump_systems_enabled'] = true
            data['solar_systems_enabled'] = true
            data['other_systems_enabled'] = true
        }
        if(!data?.primary_user_id || data?.primary_user_id === primaryIndividual.value){
            delete data?.['primary_user_id']
        }

        const filesToUpload = data?.documents
            ?.filter(doc => doc?.files?.length > 0)
            ?.flatMap(doc => doc?.files) ?? [];

        if (filesToUpload.length > 0) {
            try {
                await Promise.all(
                    filesToUpload.map(async (file) => {
                        if (!file.base64) return;
                        const uploadData: UploadDataType = await getUploadUrl();
                        const response = await FileHelpers.upload(file, uploadData);

                        // fetch() resolves on a 4xx/5xx from S3, so an unsuccessful
                        // response has to be checked explicitly. Skipping this check
                        // let the file metadata be sent with no aws_url, and the API
                        // then created a document row with no file. See CUS-1037.
                        if (!response?.ok) {
                            throw new Error(
                                `"${file.name}" could not be uploaded. Please try again.`
                            );
                        }

                        file.aws_url = uploadData.key;
                        delete file.base64;
                    })
                );
            } catch (error: any) {
                const shapedError = {
                    response: {
                        data: {
                            message: error.message || 'Upload failed'
                        }
                    }
                };
                return Promise.reject(shapedError);
            }
        };

        return isEdit
            ? axios.patch(
                route(API_ENDPOINTS.ADMIN.COMPANY.UPDATE, {companyId: companyId || ''}),
                data
            )
            : axios.post(route(API_ENDPOINTS.ADMIN.COMPANY.STORE), data);
    };

    const handleChange = (index: number, field: string, value: unknown) => {
        const updatedDocuments = [...store?.documents];
        updatedDocuments[index][field] = value;
        setValues({ documents: updatedDocuments });
    };

    // Form Initialization
    const form = useForm(handleSubmit, defaultData);
    const { Submit, isSuccess, data: submissionData, errors, store, setValues } = form;
    const formRef = useScrollToError(form?.errors);

    // Effects
    useEffect(() => {
        async function fetchCompanies() {
            setDisabled(true);
            try {
                const response = await axios.get(
                    route(API_ENDPOINTS.GENERIC.SELECT.COMPANIES + `?types[]=${CompanyTypes.DISTRIBUTOR}`)
                );
                dispatchSelects({type: 'SET_COMPANIES', payload: response.data || []});
            } catch (err: any) {
                customToast({
                    title: err?.response?.data?.message 
                        || 'Error fetching distributors',
                    variant: Variants.Danger
                });
            } finally {
                setDisabled(false);
            }
        }
        if (themes.isToshiba && isSuperAdminOrPlatformManager 
            && form?.store?.type === CompanyTypes.INSTALLER) {
            fetchCompanies();
        } 
    }, [form?.store?.type, themes.isToshiba, isSuperAdminOrPlatformManager]);

    useLayoutEffect(() => {
        if (companyId) loadCompanyData();
        // if distribution manager, set type to distributor
        if (user?.role_name === Roles.DISTRIBUTION_MANAGER) {
            form?.setValues({type: CompanyTypes.DISTRIBUTOR});
        } else {
            form?.setValues({type: CompanyTypes.INSTALLER});
        }
    }, [companyId]);

    useEffect(() => {
        if (data) updateSelectOptions(data);
    }, [data]);

    useEffect(() => {
        if (isSuccess) navigate(`/companies/${submissionData?.id}`);
    }, [isSuccess]);

    // misc
    const loadCompanyData = async () => {
        setLoaders({fetchingCompany: true});
        try {
            const response = await axios.get(
                route(API_ENDPOINTS.ADMIN.COMPANY.EDIT, {companyId: companyId || ''})
            );
            const { primary_individual, ...rest } = response?.data?.data || {};
            form?.setValues({ ...rest, role_id: user?.role_id });
            setPrimaryIndividual({
                label: primary_individual?.name,
                value: primary_individual?.id,
                userId: primary_individual?.user?.id,
            });

            await fetchRoles(response?.data?.data?.type);
        } catch (error: any) {
            handleError(error);
        } finally {
            setLoaders({fetchingCompany: false});
        }
    };

    const handleGasSafeLookupChange = (data: any) => {
		const gasSafeCompanyName = data?.company?.name;

		const nameMismatch =
			gasSafeCompanyName && form?.store.name !== gasSafeCompanyName;

		if (nameMismatch) {
			form?.setValues({
				name: gasSafeCompanyName,
			});

			customToast({
				variant: Variants.Warning,
				title: crud?.alerts?.gas_safe?.company_name_updated || 'Your company name has been updated to match the Gas Safe number details.',
			});

			setGasSafeNameMismatch(
				crud?.alerts?.gas_safe?.company_name_mismatch || 'The details on the Gas Safe licence card that you provide will be checked against the registration details that you provide. If those details don’t match, your registration will need to be verified by a platform admin. Please make sure that the name you provide is identical to the name on your licence card.'
			);
		} else {
			setGasSafeNameMismatch('');
		}
	};

    const updateSelectOptions = (data: any) => {
        dispatchSelects({
            type: 'SET_DISTRIBUTOR_CLAIM_TYPES',
            payload: data?.distributor_claim_types?.data || [],
        });
        dispatchSelects({type: 'SET_ROLES', payload: data?.roles?.data || []});
    };

    const fetchRoles = async (type: string) => {
        if (type) {
            try {
                const response = await axios.get(
                    route(API_ENDPOINTS.GENERIC.SELECT.COMPANY_TYPE_ROLES + `?type=${type}`)
                );
                
                dispatchSelects({type: 'SET_ROLES', payload: response?.data?.data || []});
            } catch (error: any) {
                handleError(error);
            }
        }
    };

    const addDocument = () => {
        setValues({ documents: [
            ...store?.documents, { 
                document_type: '', 
                expiry_date: '',
                files: [] 
            }
        ] });
    };

    const removeDocument = (index: number) => {
        const updatedDocuments = store?.documents.filter(
			(_, i) => i !== index
		);
        setValues({ documents: updatedDocuments });
    };

    const getUploadUrl = async () => {
		try {
			const response = await axios.get(
				route(API_ENDPOINTS.GENERIC.DOCUMENT.UPLOAD_URL)
			);

			return response?.data?.data;
		} catch (err: any) {
			// Throw rather than return undefined. Callers treat a missing upload
			// URL as a failed upload and stop the save, instead of carrying on
			// and saving a document with no file against it. See CUS-1037.
			throw new Error(
				err?.response?.data?.message
					|| err.message
					|| 'Could not start the file upload. Please try again.'
			);
		}
	};

    const renderSystemTypes = () => {
        let errorsPresent = false;
        if (!!errors) {
            if (
                (!!errors.gas_boiler_systems_enabled || !!errors.heat_pump_systems_enabled || !!errors.solar_systems_enabled || !!errors.other_systems_enabled) &&
                (!form?.store?.gas_boiler_systems_enabled && !form?.store?.heat_pump_systems_enabled && !form?.store?.solar_systems_enabled && !form?.store?.other_systems_enabled)
            ) {
                errorsPresent = true;
            }
        }

        return (
            <div className="d-flex gap-4 mb-4">
                <SelectableCardInput
                    className="m-0"
                    value={form?.store?.gas_boiler_systems_enabled}
                    imageSrc={ViessmannGas}
                    label={StringHelpers.title(
                        fields?.company_gas_boiler_systems_enabled
                    )}
                    isSelected={form?.store?.gas_boiler_systems_enabled}
                    onChange={(value: any) => {
                        form?.setValues({
                            gas_boiler_systems_enabled: !value,
                        });
                    }}
                    errorField={errorsPresent}
                />
                <SelectableCardInput
                    className="m-0"
                    value={form?.store?.heat_pump_systems_enabled}
                    imageSrc={ViessmannHeatpumps}
                    label={StringHelpers.title(
                        fields?.company_heat_pump_systems_enabled
                    )}
                    isSelected={form?.store?.heat_pump_systems_enabled}
                    onChange={(value: any) => {
                        form?.setValues({
                            heat_pump_systems_enabled: !value,
                        });
                    }}
                    errorField={errorsPresent}
                />
                <SelectableCardInput
                    className="m-0"
                    value={form?.store?.solar_systems_enabled}
                    imageSrc={ViessmannSolar}
                    label={StringHelpers.title(
                        fields?.company_solar_systems_enabled
                    )}
                    isSelected={form?.store?.solar_systems_enabled}
                    onChange={(value: any) => {
                        form?.setValues({
                            solar_systems_enabled: !value,
                        });
                    }}
                    errorField={errorsPresent}
                />
                <SelectableCardInput
                    className="m-0"
                    value={form?.store?.other_systems_enabled}
                    imageSrc={ViessmannElectric}
                    label={StringHelpers.title(
                        fields?.company_other_systems_enabled
                    )}
                    isSelected={form?.store?.other_systems_enabled}
                    onChange={(value: any) => {
                        form?.setValues({
                            other_systems_enabled: !value,
                        });
                    }}
                    errorField={errorsPresent}
                />
            </div>
        )
    }

    const renderToshibaInstallerFields = () => {
        return (
            <div className='mt-4'>
                <div className="row">
                    <ColumnLayout>
                        {form.Input({
                            name: 'fgas_refcom_standard_number',
                            label: fields?.fgas_refcom_standard_number
                                || 'FGAS / REFCOM standard number',
                            type: 'text',
                        })}
                    </ColumnLayout>
                    <ColumnLayout>
                        {form.Input({
                            name: 'account_number',
                            label: StringHelpers.title(
                                fields?.account_number || 'Account number'
                            ),
                            type: 'text',
                        })}
                    </ColumnLayout>
                </div>
            </div>
        );
    }

    const renderToshibaDistributorFields = () => {
        return (
            <>
                {form.Input({
                    name: 'dispatch_note_field_label',
                    label: StringHelpers.title(
                        fields?.dispatch_note_field_label ||
                        'Dispatch note field label'
                    ),
                    type: 'text',
                })}
                {form.Input({
                    name: 'distributor_claim_type',
                    label: StringHelpers.title(
                        fields?.distributor_claim_type || 'Distributor claim type'
                    ),
                    type: 'select',
                    placeholder: 'Select',
                    options: selects?.distributor_claim_types,
                })}
                {form.Input({
                    name: 'distributor_claim_inbox_email',
                    label: StringHelpers.title(
                        fields?.distributor_claim_inbox_email ||
                        'Distributor claim inbox email'
                    ),
                    type: 'text',
                })}
                <FormInput
                    type="select"
                    name="dispatch_note_required"
                    label={StringHelpers.title(
                        fields?.dispatch_note_required || 'Dispatch note required'
                    )}
                    placeholder={StringHelpers.title(
                        replaceKeyWithValue(crud?.placeholders?.select, 'model', '')
                    )}
                    value={form?.store?.dispatch_note_required}
                    onChange={(v) =>
                        form?.setValues({
                            dispatch_note_required:
                                typeof v === 'string' ? JSON.parse(v) : v,
                        })
                    }
                    options={booleanOptions}
                />
            </>
        )
    }

    const renderGasSafeFields = () => {
        return (
            <>
                <SubTitle
                    title={
                        (crud?.models?.company || 'Company') +
                        ' ' + (crud?.sub_titles?.gas_safe || 'GasSafe') +
                        ' ' + (crud?.sub_titles?.details || 'Details')
                    }
                    className="my-3 w-100"
                    style={{color: '#464C5E'}}
                />
                <GasSafeLookup
                    fetchOnLoad={isEdit}
                    displayCard
                    onGasSafeNumberChange={(value) => {
                        form?.setValues({
                            gas_safe_number: value,
                        });
                    }}
                    onGasSafeConfirmedChange={(value) => {
                        form?.setValues({
                            gas_safe_confirmed: value,
                        });
                    }}
                    disabled={disabled}
                    gasSafeNumber={form?.store?.gas_safe_number}
                    gasSafeConfirmed={form?.store?.gas_safe_confirmed}
                    labels={{
                        gasSafeNumber: fields?.gas_safe_number,
                        gasSafeConfirmed: fields?.gas_safe_confirmation_generic,
                    }}
                    errors={{
                        gasSafeNumber: form?.errors?.gas_safe_number,
                        gasSafeConfirmed: form?.errors?.gas_safe_confirmed,
                    }}
                    type={'company'}
                    warningMessage={gasSafeNameMismatch}
                    onGasSafeLookupChange={
						handleGasSafeLookupChange
					}
                />
            </>
        )
    }

    const renderPrimaryUserFields = () => {
        return (
            <Card>
                <CardBody>
                    <SubTitle
                        className="mb-3"
                        style={{color: '#464C5E'}}
                        title={StringHelpers.title(
                            `${crud?.sub_titles?.primary} ${crud?.models?.user}`
                        )}
                    />
                    <div className="row">
                        <ColumnLayout>
                            {form.Input({
                                name: 'first_name',
                                label: StringHelpers.title(
                                    fields?.first_name || 'First Name'
                                ),
                                type: 'text',
                            })}
                        </ColumnLayout>
                        <ColumnLayout>
                            {form.Input({
                                name: 'last_name',
                                label: StringHelpers.title(fields?.last_name || 'Last Name'),
                                type: 'text',
                            })}
                        </ColumnLayout>
                    </div>
                    <div className="row">
                        <ColumnLayout>
                            {form.Input({
                                name: 'email',
                                label: StringHelpers.title(fields?.email || 'Email'),
                                type: 'email',
                            })}
                        </ColumnLayout>
                        {!checkTheme(Themes.Toshiba) && 
                            <ColumnLayout>
                                <FormInput
                                    type="text"
                                    label={StringHelpers.title(fields?.username || 'Username')}
                                    name="username"
                                    value={form?.store?.email}
                                    disabled
                                />
                            </ColumnLayout>
                        }
                    </div>

                    <div className="col-12 mb-3">
                        <PhoneNumberField
                            noMargin
                            label={StringHelpers.title(
                                fields.mobile_phone_number || 'Mobile number'
                            )}
                            name="mobile_phone_number"
                            value={form?.store?.mobile_phone_number}
                            onChange={(value) =>
                                form.setValues({
                                    mobile_phone_number: value,
                                })
                            }
                            disabled={disabled}
                            errorMessages={form?.errors?.mobile_phone_number || form?.errors?.mobile_phone_prefix}
                            prefixValue={form?.store?.mobile_phone_prefix}
                            prefixOnChange={(value) =>
                                form.setValues({
                                    mobile_phone_prefix: value,
                                })
                            }
                        />
                    </div>

                    {/* Landline Details */}
                    <div className="col-12 mb-5">
                        <PhoneNumberField
                            noMargin
                            label={StringHelpers.title(
                                fields.landline_phone_number || 'Landline number'
                            )}
                            name="landline_phone_number"
                            value={form?.store?.landline_phone_number}
                            onChange={(value) =>
                                form.setValues({
                                    landline_phone_number: value,
                                })
                            }
                            disabled={disabled}
                            errorMessages={form?.errors?.landline_phone_number || form?.errors?.landline_phone_prefix}
                            prefixValue={form?.store?.landline_phone_prefix}
                            prefixOnChange={(value) =>
                                form.setValues({
                                    landline_phone_prefix: value,
                                })
                            }
                        />
                    </div>
                    <PostcodeLookup
                        prefix="user_"
                        data={form?.store}
                        setData={form?.setValues}
                        errors={form?.errors}
                    />
                    {form.store.gas_boiler_systems_enabled && (<>
                        <SubTitle
                            title={
                                (crud?.models?.user || 'User') +
                                ' ' + (crud?.sub_titles?.gas_safe || 'GasSafe') +
                                ' ' + (crud?.sub_titles?.details || 'Details')
                            }
                            className="my-3 mt-5 w-100"
                            style={{color: '#464C5E'}}
                        />
                        <GasSafeLookup
                            displayCard
                            onGasSafeNumberChange={(value) => {
                                form?.setValues({
                                    gas_safe_licence_card_number: value,
                                });
                            }}
                            onGasSafeConfirmedChange={(value) => {
                                form?.setValues({
                                    user_gas_safe_confirmed: value,
                                });
                            }}
                            disabled={disabled}
                            gasSafeNumber={form?.store?.gas_safe_licence_card_number}
                            gasSafeConfirmed={form?.store?.user_gas_safe_confirmed}
                            labels={{
                                gasSafeNumber: fields?.gas_safe_licence_card_number,
                                gasSafeConfirmed: fields?.gas_safe_confirmation_generic,
                            }}
                            errors={{
                                gasSafeNumber: form?.errors?.gas_safe_licence_card_number,
                                gasSafeConfirmed: form?.errors?.user_gas_safe_confirmed,
                            }}
                            type={'engineer'}
                        />
                    </>)}
                </CardBody>
            </Card>
        )
    }

    return (
        <PageLayout title={title}>
            <WidthConstrainedContainer>
                {componentLoading ? (
                    <div className="d-flex justify-content-center align-items-center vh-100">
                        <Spinner/>
                    </div>
                ) : (
                    <>
                        <div ref={formRef}>
                            {/* Details */}
                            <Card>
                                <CardBody>
                                    <SubTitle
                                        className="mb-3"
                                        style={{color: '#464C5E'}}
                                        title={StringHelpers.title(
                                            (crud?.models?.company || 'Company') + ' ' +
                                            (crud?.sub_titles?.details || 'Details'))
                                        }
                                    />
                                    {form.Input({
                                        name: 'name',
                                        label: StringHelpers.title(fields?.name || 'Name'),
                                        type: 'text',
                                        disable: themes.isToshiba && isEdit && !isSuperAdminOrPlatformManager,
                                    })}
                                    {isEdit && form.Input({
                                        name: "primary_user_id",
                                        type: "select",
                                        label: StringHelpers.title(
                                            `${crud?.sub_titles?.primary} ${crud?.models?.user}`
                                        ),
                                        options: companyUsers,
                                    })}
                                    {themes.isToshiba &&
                                        !isEdit && (
                                            <FormInput
                                                type="select"
                                                name="type"
                                                label={StringHelpers.title(fields?.type || 'Type')}
                                                placeholder={StringHelpers.title(
                                                    replaceKeyWithValue(crud?.placeholders?.select, 'model', '')
                                                )}
                                                value={form?.store?.type}
                                                onChange={(v) => {
                                                    form?.setValues({type: v as any});
                                                    fetchRoles(v as any);
                                                }}
                                                options={selects?.company_types}
                                                errorMessages={errors?.type}
                                            />
                                        )
                                    }
                                    {themes.isToshiba && isSuperAdminOrPlatformManager 
                                    && form?.store?.type === CompanyTypes.INSTALLER && (
                                        <FormInput
                                            type="searchable-select"
                                            name="distributor_ids"
                                            label={StringHelpers.title(
                                                fields?.distributor_ids || 'Distributors'
                                            )}
                                            placeholder={StringHelpers.title(
                                                replaceKeyWithValue(crud?.placeholders?.select, 'model', '')
                                            )}
                                            value={form?.store?.distributor_ids}
                                            onChange={(v) =>
                                                form?.setValues({
                                                    distributor_ids: v as any,
                                                })
                                            }
                                            isMulti
                                            options={selects?.companies?.data}
                                            disabled={
                                                disabled || user?.role_name === Roles.DISTRIBUTION_MANAGER
                                            }
                                            errorMessages={errors?.distributor_ids}
                                        />
                                    )}
                                    <PostcodeLookup
                                        data={form?.store}
                                        setData={form?.setValues}
                                        errors={form?.errors}
                                        manualEntry
                                        onManualEntry={(isManualEntry) => {
                                            setIsManualEntry(isManualEntry);
                                        }}
                                    />
                                    {isManualEntry &&
                                        form.Input({
                                            name: 'local_authority_code',
                                            label: StringHelpers.title(
                                                fields?.local_authority_code ||
                                                    'Local authority code'
                                            ),
                                            type: 'text',
                                            description: fields.local_authority_code_help,
                                            disable: !isManualEntry
                                        })
                                    }
                                    <div className="row mt-3">
                                        <ColumnLayout>
                                            <FormInput
                                                type="select"
                                                name="customer_email_enabled"
                                                label={StringHelpers.title(
                                                    fields?.customer_email_enabled || 'Customer email enabled'
                                                )}
                                                placeholder={StringHelpers.title(
                                                    replaceKeyWithValue(crud?.placeholders?.select, 'model', '')
                                                )}
                                                value={form?.store?.customer_email_enabled}
                                                onChange={(v) =>
                                                    form?.setValues({
                                                        customer_email_enabled:
                                                            typeof v === 'string' ? JSON.parse(v) : v,
                                                    })
                                                }
                                                options={booleanOptions}
                                            />
                                        </ColumnLayout>
                                        <ColumnLayout>
                                            <FormInput
                                                type="select"
                                                name="system_access_enabled"
                                                label={StringHelpers.title(fields?.system_access_enabled)}
                                                placeholder={StringHelpers.title(
                                                    replaceKeyWithValue(crud?.placeholders?.select, 'model', '')
                                                )}
                                                value={form?.store?.system_access_enabled}
                                                onChange={(v) =>
                                                    form?.setValues({
                                                        system_access_enabled:
                                                            typeof v === 'string' ? JSON.parse(v) : v,
                                                    })
                                                }
                                                options={booleanOptions}
                                            />
                                        </ColumnLayout>
                                    </div>
                                    {/* Additional Platform fields */}
                                    {themes.isViessmann && (<>
                                        <SubTitle
                                            className="mb-3 mt-2"
                                            style={{color: '#464C5E'}}
                                            title={StringHelpers.title(
                                                `${crud?.sub_titles?.additional_platform} ${crud?.sub_titles?.details}`
                                            )}
                                        />
                                        {/* system types */}
                                        {renderSystemTypes()}
                                        {themes.isViessmann && form?.Input({
                                            name: 'sap_number',
                                            label: StringHelpers.title(fields?.sap_number || 'SAP number'),
                                            type: 'text',
                                        })}
                                        {themes.isViessmann && (form?.store.solar_systems_enabled || form?.store.heat_pump_systems_enabled) &&
                                            form?.Input({
                                                name: 'mcs_number',
                                                label: StringHelpers.title(fields?.mcs_number || 'MCS number'),
                                                type: 'text',
                                            })
                                        }
                                        {themes.isViessmann && store?.heat_pump_systems_enabled && (
                                            <div className='row'>
                                                <ColumnLayout>
                                                    <FormInput
                                                        type='select'
                                                        name='vipartner'
                                                        label={fields?.vipartner || 'VIPartner'}
                                                        placeholder={StringHelpers.title(
                                                            replaceKeyWithValue(crud?.placeholders?.select, 'model', '')
                                                        )}
                                                        value={store?.vipartner}
                                                        onChange={(v) => {
                                                            setValues({
                                                                vipartner:
                                                                    typeof v === 'string' ? JSON.parse(v) : v,
                                                                vipartner_ends_at:
                                                                    v === 'true' ? store.vipartner_ends_at : ''
                                                            })
                                                        }}
                                                        options={booleanOptions}
                                                    />
                                                </ColumnLayout>
                                                {store?.vipartner && (
                                                    <ColumnLayout>
                                                        <FormInput
                                                            type='date'
                                                            name='vipartner_ends_at'
                                                            label={fields?.vipartner_ends_at || 'VIPartner Ends At (Exclusive)'}
                                                            value={store?.vipartner_ends_at}
                                                            onChange={(v) => 
                                                                setValues({
                                                                    vipartner_ends_at: v === '__/__/____' ? '' : v as string
                                                                })
                                                            }
                                                            errorMessages={errors?.vipartner_ends_at}
                                                        />
                                                    </ColumnLayout>
                                                )}
                                            </div>
                                        )}
                                    </>)}
                                    {form?.store?.type === CompanyTypes.INSTALLER && themes.isToshiba && renderToshibaInstallerFields()}
                                    {form?.store?.type === CompanyTypes.DISTRIBUTOR && themes.isToshiba && renderToshibaDistributorFields()}
                                    {themes.isViessmann && form.store.gas_boiler_systems_enabled && renderGasSafeFields()}
                                </CardBody>
                            </Card>
                            {/* Primary User */}
                            {!isEdit && renderPrimaryUserFields()}
                            {themes.isToshiba && (
                                <Card>
                                    <CardBody>
                                        <SubTitle
                                            style={{color: '#464C5E'}}
                                            title={crud?.sub_titles?.documents || 'Documents'}
                                            className='mb-3'
                                        />
                                        {store?.documents?.map((document, index) => {
                                            return (
                                                <Card key={index}>
                                                    <CardBody>
                                                        {store?.documents?.length > 1 && 
                                                            !document?.id && (
                                                            <div className="d-flex justify-content-end mb-4">
                                                                <Button
                                                                    label={StringHelpers.title(
                                                                        crud?.buttons?.delete?.default ||
                                                                            'Delete'
                                                                    )}
                                                                    onClick={() => removeDocument(index)}
                                                                    disabled={disabled}
                                                                    type={ButtonTypes.Outline}
                                                                    variant={Variants.Danger}
                                                                    size={Sizes.Small}
                                                                    rightIcon={Icons.DELETE}
                                                                />
                                                            </div>
                                                        )}
                                                        <MultiFileInput
                                                            value={document.files}
                                                            maxFiles={3}
                                                            onChange={(value) =>
                                                                setValues({
                                                                    documents: store?.documents?.map((doc, idx) =>
                                                                        idx === index
                                                                            ? { ...doc, files: value ?? [] }
                                                                            : { ...doc, files: doc.files ?? [] }
                                                                    ),
                                                                })
                                                            }
                                                            label={StringHelpers.title(fields?.files || 'files')}
                                                            onReject={(value) => {
                                                                customToast({
                                                                    title: value || "Error with the files you've selected",
                                                                    variant: Variants.Danger
                                                                });
                                                            }}
                                                            disabled={isEdit && !!document?.id}
                                                            errorField={
                                                                Object.hasOwn(errors || {}, `documents.${index}.document_type`) ||
                                                                Object.hasOwn(errors || {}, `documents.${index}.expiry_date`)
                                                            }
                                                        />
                                                        <div className='row'>
                                                            <div className='col-12 col-lg-6'>
                                                                <FormInput
                                                                    type='select'
                                                                    label={StringHelpers.title(
                                                                        fields?.type || 'Type'
                                                                    )}
                                                                    placeholder={StringHelpers.title(
                                                                        fields?.select || 'Select'
                                                                    )}
                                                                    name='document_type'
                                                                    value={document.document_type}
                                                                    options={documentTypes?.map((docType) => {
                                                                        const alreadySelected = store?.documents.some((doc) => 
                                                                            doc.document_type === docType.value)

                                                                        return ({
                                                                            label: docType.label,
                                                                            value: docType.value,
                                                                            disabled: alreadySelected
                                                                        })
                                                                    })}
                                                                    disabled={disabled}
                                                                    onChange={(value) => {
                                                                        handleChange(index, 'document_type', value);
                                                                    }}
                                                                    errorMessages={
                                                                        !document.document_type
                                                                        ? errors?.[`documents.${index}.expiry_date`] 
                                                                            || errors?.[`documents.${index}.files.0.name`]
                                                                        : undefined
                                                                    }
                                                                />
                                                            </div>
                                                            <div className='col-12 col-lg-6'>
                                                                <FormInput
                                                                    type='date'
                                                                    name='expiry_date'
                                                                    label={fields?.expiry_date || 'Expiry Date'}
                                                                    value={document.expiry_date}
                                                                    onChange={(value) => {
                                                                        if (value === '__/__/____') value = ''
                                                                        handleChange(index, 'expiry_date', value);
                                                                    }}
                                                                    disabled={disabled}
                                                                    errorMessages={
                                                                        !document.expiry_date
                                                                        ? errors?.[`documents.${index}.document_type`] 
                                                                            || errors?.[`documents.${index}.files.0.name`]
                                                                        : undefined
                                                                    }           
                                                                />
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            )
                                        })}
                                        <div className="d-flex justify-content-end">
                                            <Button
                                                label={
                                                    `${crud?.buttons?.add?.default 
                                                        || 'Add'
                                                    } ${crud?.models?.document 
                                                        || 'Document'}`
                                                }
                                                type={ButtonTypes.Outline}
                                                disabled={disabled}
                                                variant={Variants.Dark}
                                                size={Sizes.Small}
                                                onClick={addDocument}
                                            />
                                        </div>
                                    </CardBody>
                                </Card>
                            )}
                        </div>

                        <BottomAnchoredControls>
                            <Button
                                label={StringHelpers.title(crud?.buttons.back.default || 'Back')}
                                onClick={() =>
                                    isEdit ? navigate(`/companies/${companyId}`) : navigate('/companies')
                                }
                                variant={Variants.Dark}
                                type={ButtonTypes.Outline}
                                className={'me-4'}
                                disabled={disabled}
                            />
                            <Submit
                                defaultLabel={StringHelpers.title(
                                    isEdit
                                        ? crud?.buttons.edit.default || 'Update'
                                        : crud?.buttons.create.default || 'Create'
                                )}
                                loadingLabel={StringHelpers.title(
                                    isEdit
                                        ? crud?.buttons.edit.submitting || 'Updating...'
                                        : crud?.buttons.create.submitting || 'Creating...'
                                )}
                                useOnMouseDown
                            />
                        </BottomAnchoredControls>
                    </>
                )}
            </WidthConstrainedContainer>
        </PageLayout>
    );
}

export default CompanyForm;
